/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { SessionErrorCode } from "./PlaybackErrorCodes";

/** What a session-create failure means, and how playback should respond. */
export type TSessionFailure = {
  /**
   * Whether playback should continue on the URL already resolved.
   *
   * True only for failures that say nothing about the viewer's right to
   * watch. A denial is never fail-open.
   */
  failOpen: boolean;

  /** The error code to record, and to surface when failing closed. */
  code: string;

  /** Why this classification was chosen, for logging. */
  reason: string;
};

/** HTTP statuses that carry a policy decision rather than a fault. */
const POLICY_STATUS_CODES: ReadonlyMap<number, string> = new Map<
  number,
  string
>([
  [401, SessionErrorCode.NOT_AUTHENTICATED],
  [403, SessionErrorCode.NOT_AUTHORIZED],
  [404, SessionErrorCode.NO_SESSION],
  [409, SessionErrorCode.TOO_MANY_STREAMS],
  [412, SessionErrorCode.NOT_REGISTERED],
  [429, SessionErrorCode.TOO_MANY_STREAMS_FOR_RESOURCE]
]);

/**
 * Decides whether a session-create failure should stop playback.
 *
 * The distinction is not severity but *meaning*. A 403 says the viewer may
 * not watch this, and playing anyway would defeat the entitlement check that
 * the session exists to perform. A 503 says the session service is having a
 * bad day, which is not a statement about the viewer at all — and for a
 * provider whose streams are unencrypted and freely available, blocking
 * playback over it denies the viewer content nobody objected to them seeing.
 *
 * Unrecognised failures fail **open**. That is a deliberate asymmetry: the
 * shapes the session service returns for real denials are not yet pinned
 * down, so this errs toward the failure mode that keeps a legitimate viewer
 * watching. It is safe only where it is applied — providers whose content
 * carries no entitlement of its own — and must be revisited if fail-open is
 * ever extended past them.
 */
export class SessionFailureClassifier {
  /**
   * Classifies a caught session-create failure.
   *
   * @param error - Whatever the session call rejected with.
   * @returns The meaning of the failure and how to respond to it.
   */
  public static classify(error: unknown): TSessionFailure {
    const status = SessionFailureClassifier.readStatus(error);

    if (status !== undefined) {
      const policyCode = POLICY_STATUS_CODES.get(status);

      if (policyCode !== undefined) {
        return {
          failOpen: false,
          code: policyCode,
          reason: "policy denial (HTTP " + String(status) + ")"
        };
      }

      if (status >= 500) {
        return {
          failOpen: true,
          code: SessionErrorCode.INTERNAL_ERROR,
          reason: "server error (HTTP " + String(status) + ")"
        };
      }

      // Every other 4xx is a malformed request: our fault, not the viewer's,
      // and nothing they can act on.
      if (status >= 400) {
        return {
          failOpen: true,
          code: SessionErrorCode.GENERIC_LIVE_ERROR,
          reason: "bad request (HTTP " + String(status) + ")"
        };
      }
    }

    return {
      failOpen: true,
      code: SessionErrorCode.UNKNOWN_ERROR,
      reason: "unrecognised failure"
    };
  }

  /**
   * Reads an HTTP status from a rejection.
   *
   * Duck-typed on purpose: the session service surfaces failures in more than
   * one shape, and none of them is a type this layer can import.
   */
  private static readStatus(error: unknown): number | undefined {
    if (typeof error !== "object" || error === null) {
      return undefined;
    }

    const candidate = error as {
      status?: unknown;
      statusCode?: unknown;
      response?: { status?: unknown };
    };

    if (typeof candidate.status === "number") {
      return candidate.status;
    }

    if (typeof candidate.statusCode === "number") {
      return candidate.statusCode;
    }

    if (
      typeof candidate.response === "object" &&
      candidate.response !== null &&
      typeof candidate.response.status === "number"
    ) {
      return candidate.response.status;
    }

    return undefined;
  }
}
